import { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Card } from '@altermaind/web/ui';
import { CreditViewValue } from '@/_shared/components/CreditViewValue';
import { useDrawer } from '@/_shared/components/Drawer';
import { Column, usePaginatedTable } from '@/_shared/components/Table';
import { PaginatedTable } from '@/_shared/components/Table/PaginatedTable';
import { StyledTable, StyledTableDrawer } from '@/_shared/components/Table/StyledTable';
import { TableHeader } from '@/_shared/components/TableHeader';
import { ValueInTable } from '@/_shared/components/ValueInTable';
import { postEmployerCreditsHistoryCall } from '@/credits/state/api';
import { PAGE_SIZE_EMPLOYER_CREDITS_HISTORY } from '@/people/state/constants';
import { FilterHistory } from './FilterHistory';

type Props = {
  emailEmployer: string;
};

export const HistoryList: FC<Props> = ({ emailEmployer }) => {
  const t = useTranslations('people.details.credits.history.table');
  const tShared = useTranslations('credits');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filters, setFilters] = useState<{ month?: number; year?: number }>();
  const { isOpen, close, ...drawerProps } = useDrawer();

  const {
    execute: executePostEmployerCreditsHistory,
    loading: loadingPostEmployerCreditsHistory,
    data,
    totalItems,
  } = postEmployerCreditsHistoryCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_EMPLOYER_CREDITS_HISTORY,
    totalItems: totalItems || 0,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  const columns: Column[] = [
    {
      key: 'activity',
      title: t('activity'),
      renderValue: item => <ValueInTable>{`${tShared(`${item.eventType}`)}`}</ValueInTable>,
    },
    {
      key: 'credits',
      title: t('credits'),
      renderValue: item => (
        <ValueInTable color={item.credits > 0 ? 'success' : 'error/bright'}>
          <CreditViewValue value={item.credits} eventType={item.eventType} />
        </ValueInTable>
      ),
    },
    {
      key: 'date',
      title: t('date'),
      renderValue: item => (
        <ValueInTable>{dayjs(item.timestamp).format('DD/MM/YYYY')}</ValueInTable>
      ),
    },
  ];

  useEffect(() => {
    // Se filters?.month è una stringa come "january", convertila in numero (1-12)
    executePostEmployerCreditsHistory({
      start: 0,
      length: PAGE_SIZE_EMPLOYER_CREDITS_HISTORY,
      email: emailEmployer,
      year: filters?.year || dayjs().year(),
      month: Number(filters?.month) || undefined,
    });
  }, [executePostEmployerCreditsHistory, filters, emailEmployer]);

  return (
    <Card overflow='visible' mt='s' display='flex' flexDirection='column'>
      <TableHeader
        title={t('title')}
        filtersComponent={<FilterHistory setFilters={setFilters} filters={filters} />}
      />
      <StyledTable hasElement drawer={{ ...drawerProps, isOpen, close, instances: drawers }}>
        <PaginatedTable
          loading={loadingPostEmployerCreditsHistory}
          header-data-testid='table-employer-credits-history-header'
          row-data-testid='table-employer-credits-history-row'
          columns={columns}
          pageSize={PAGE_SIZE_EMPLOYER_CREDITS_HISTORY}
          call={{ ...postEmployerCreditsHistoryCall }}
          onRowClick={() => {
            /* empty */
          }}
          rowWrapperProps={(employee: string) => {
            return employee === ''
              ? {
                  backgroundColor: 'grey/dark/background',
                  borderRadius: '10px',
                  position: 'relative',
                }
              : {};
          }}
          paginedProps={paginedProps}
          skeletonProps={{
            hasHeaderRow1: false,
            hasHeaderRow2: true,
            rows: 1,
          }}
          hasHover
        />
      </StyledTable>
    </Card>
  );
};
